import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2, Send, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneField } from '@/components/booking/phone-field';
import { countryFieldOf, resolveCountry, guessCountryIso, splitPhone, phoneProblem } from '@/lib/countries';
import { CustomFieldInput } from '@/components/booking/custom-field-input';
import { useTracking } from '@/hooks/use-tracking';
import { usePartialCapture } from '@/hooks/use-partial-capture';
import { usePhoneVerification } from '@/hooks/use-phone-verification';
import { VerifyGate } from '@/components/booking/verify-gate';
import { emit as emitParent } from '@/lib/iframe-events';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Format rules - kept in sync with the server (api/src/routes/leads.js).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function isValidEmail(e) { return EMAIL_RE.test(String(e || '').trim()); }

// Validate the built-in identity fields. A lead form does NOT force both email
// and phone - it needs a name plus at least one valid way to reach the lead.
// Whatever IS filled in must still be well-formed. Mirrors the server floor.
function validateBasics(formData, cfg) {
  if (!formData.first_name?.trim()) return 'First name is required.';
  const hasEmail = !!formData.email?.trim();
  const hasPhone = !!formData.phone?.trim();
  // A form can insist on both. The server enforces this too - the message here
  // just saves the visitor a round trip.
  if (cfg?.require_email && !hasEmail) return 'Please add your email address.';
  if (cfg?.require_phone && !hasPhone) return 'Please add your phone number.';
  if (!hasEmail && !hasPhone) return 'Please add an email or a phone number.';
  if (hasEmail && !isValidEmail(formData.email)) return 'Please enter a valid email address.';
  // Judged per country: the picker fixes the code, this fixes the digit count
  // ("+91 95993385280" has one digit too many, and used to sail through).
  if (hasPhone) { const p = phoneProblem(formData.phone); if (p) return p; }
  return null;
}

// Validate every required custom field from the Form Builder schema.
function validateRequiredCustomFields(fields, formData) {
  for (const f of fields || []) {
    if (!f.required) continue;
    const v = formData.custom_data?.[f.id];
    if (f.type === 'checkbox') {
      if (!Array.isArray(v) || v.length === 0) return `"${f.name}": pick at least one option.`;
    } else if (!v || (typeof v === 'string' && !v.trim())) {
      return `"${f.name}" is required.`;
    }
  }
  return null;
}

// Lead-capture form: a single screen of basics + the form's custom fields, no
// scheduling. Submits straight to /api/leads (no meeting), so the lead lands in
// the CRM with full attribution and the team follows up however they like.
export default function LeadCaptureForm({ userId, formConfig }) {
  const tracking = useTracking();
  const fields = useMemo(() => formConfig?.fields || [], [formConfig]);
  // Built-in optional contact fields (default ON when the flag is absent).
  const collectCompany = formConfig?.collect_company !== false;
  const collectWebsite = formConfig?.collect_website !== false;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    custom_data: {},
  });
  // Which country the phone box starts on. Must sit AFTER formData is declared -
  // reading it above the useState is a temporal dead zone, which throws at render
  // and blanks the whole form.
  const countryFieldId = countryFieldOf(formConfig);
  const answeredCountry = countryFieldId ? formData.custom_data?.[countryFieldId] : null;
  const phoneIso = splitPhone(
    formData.phone,
    resolveCountry(answeredCountry)?.iso
      || guessCountryIso(formConfig?.default_country || formConfig?.workspace_country),
  ).iso;

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Multi-step: pages come from each field's `step` (default 1). Contact basics
  // always sit on the first page; a single step renders exactly the one-screen
  // form this has always been.
  const stepNums = useMemo(() => {
    const ns = [...new Set(fields.map((f) => (Number(f?.step) >= 1 ? Math.floor(Number(f.step)) : 1)))];
    return ns.length ? ns.sort((a, b) => a - b) : [1];
  }, [fields]);
  const [pageIdx, setPageIdx] = useState(0);
  const isLastPage = pageIdx === stepNums.length - 1;
  const pageFields = useMemo(
    () => fields.filter((f) => (Number(f?.step) >= 1 ? Math.floor(Number(f.step)) : 1) === stepNums[pageIdx]),
    [fields, stepNums, pageIdx]);
  const intro = formConfig?.step_intros?.[stepNums[pageIdx]] || null;

  function goNext() {
    setError('');
    // Validate only what this page shows: basics live on page one, and each
    // page checks its own required questions before letting go.
    if (pageIdx === 0) {
      const basicsErr = validateBasics(formData, formConfig);
      if (basicsErr) return setError(basicsErr);
    }
    const customErr = validateRequiredCustomFields(pageFields, formData);
    if (customErr) return setError(customErr);
    capturePartial(); // answers so far are worth keeping even if they stop here
    setPageIdx((i) => i + 1);
    emitParent('mm:form:step', { formId: formConfig?.id, step: stepNums[pageIdx + 1] });
  }
  function goBack() { setError(''); setPageIdx((i) => Math.max(0, i - 1)); }
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // Cold-capture: record started-but-not-submitted entries (opt-in per form).
  // Checkpoint-based - captured on email/phone blur plus a tab-hide beacon.
  const { capture: capturePartial } = usePartialCapture({ formConfig, formData, tracking, submitted });

  // Phone verification. Deliberately declared AFTER the cold-capture hook: the
  // capture fires on the phone field's blur, which happens long before anyone
  // presses Verify, so an abandoned verification is still a captured contact.
  // Blocking a submission must never mean losing sight of the visitor.
  const verification = usePhoneVerification({ formConfig, phone: formData.phone });
  // True between "they pressed the button" and "the code came back right".
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const setField = (name, value) => setFormData((d) => ({ ...d, [name]: value }));
  const setCustom = (id, value) =>
    setFormData((d) => ({ ...d, custom_data: { ...d.custom_data, [id]: value } }));
  const setOtherText = (key, value) =>
    setFormData((d) => ({ ...d, custom_data: { ...d.custom_data, [key]: value } }));

  // Tell the parent page (host site) the form came into view, so GTM can track it.
  useEffect(() => {
    if (!formConfig?.id) return;
    emitParent('mm:form:view', { formId: formConfig.id, formName: formConfig.name });
  }, [formConfig?.id, formConfig?.name]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const basicsErr = validateBasics(formData, formConfig);
    if (basicsErr) return setError(basicsErr);
    const customErr = validateRequiredCustomFields(fields, formData);
    if (customErr) return setError(customErr);
    if (formConfig?.confirm_text && !confirmed) {
      return setError('Please tick the confirmation box before submitting.');
    }
    // Everything else checked out, so now - and only now - ask for the code.
    // Sending it before the form is valid would text someone a code and then
    // refuse their submission for a missing answer.
    if (verification.active && verification.required && !verification.verified) {
      setPendingSubmit(true);
      if (verification.status === 'idle') await verification.send();
      return; // the panel is open; entering the code finishes the submission
    }

    await doSubmit();
  }

  // Resume the moment the number is proven - one press, one intent.
  useEffect(() => {
    if (pendingSubmit && verification.verified) { setPendingSubmit(false); doSubmit(); }
  }, [pendingSubmit, verification.verified]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doSubmit() {
    setError('');
    setSubmitting(true);
    try {
      const cookiesData = tracking.withEventMeta();
      const leadRes = await axios.post(`${API_URL}/api/leads`, {
        ...formData,
        cookies_data: cookiesData,
        source: cookiesData.utm_source || window.location.hostname,
        // Honour the form's assignee if one is set; otherwise let the CRM's
        // default routing place the lead (lead forms often have no owner).
        ...(userId ? { assigned_to: userId } : {}),
        form_id: formConfig?.id,
        ...(verification.token ? { verification_token: verification.token } : {}),
      });
      const leadId = leadRes.data.id;

      emitParent('mm:lead:submitted', {
        formId: formConfig?.id,
        leadId,
        fields: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          website: formData.website,
          ...formData.custom_data,
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Submit failed:', err);
      const message = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(message);
      emitParent('mm:lead:error', { formId: formConfig?.id, error: message });
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Success state --------------------------------------------------------
  if (submitted) {
    return (
      <Card className="mx-auto w-full max-w-xl shadow-elevated">
        <CardContent className="flex flex-col items-center gap-5 px-2 py-10 text-center animate-fade-in">
          <div className="flex size-16 items-center justify-center rounded-full bg-success-soft">
            <CheckCircle2 className="size-7 text-success" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-xl font-semibold tracking-tight">Thanks - we got it 🎉</h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              {formConfig?.success_message ||
                "Your details are in. Our team will reach out to you shortly."}
            </p>
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={() => window.location.reload()}
            className="text-xs text-muted-foreground"
          >
            Submit another response →
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mx-auto w-full max-w-2xl shadow-elevated">
      <CardHeader className="border-b">
        <CardTitle className="text-xl">
          {formConfig?.name || 'Get in touch'}
        </CardTitle>
        {formConfig?.description ? (
          <CardDescription>{formConfig.description}</CardDescription>
        ) : (
          <CardDescription>
            Leave your details and we&apos;ll be in touch shortly.
          </CardDescription>
        )}
      </CardHeader>

      {formConfig?.proof_text && (
        <div className="flex items-center justify-center gap-1.5 border-b border-border bg-primary-soft/40 px-6 py-2.5 text-center text-sm font-medium text-primary">
          <Sparkles className="size-3.5 shrink-0" />
          <span>{formConfig.proof_text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4 pt-5">
          {stepNums.length > 1 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Step {pageIdx + 1} of {stepNums.length}</span>
              <div className="flex gap-1">
                {stepNums.map((n, i) => (
                  <span key={n} className={`h-1 w-6 rounded-full ${i <= pageIdx ? 'bg-primary' : 'bg-muted'}`} />
                ))}
              </div>
            </div>
          )}
          {intro && (intro.headline || intro.subtext) && (
            <div className="space-y-1">
              {intro.headline && <h3 className="text-base font-semibold tracking-tight">{intro.headline}</h3>}
              {intro.subtext && <p className="text-sm text-muted-foreground">{intro.subtext}</p>}
            </div>
          )}
          {pageIdx === 0 && (<>
          {/* Basics - name required, email OR phone required (not both). */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setField('first_name', e.target.value)}
                placeholder="John"
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setField('last_name', e.target.value)}
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setField('email', e.target.value)}
              onBlur={capturePartial}
              placeholder="you@company.com"
              autoComplete="email"
            />
          </div>

          {/* Full width. Half a row minus the country chip left the number box
              narrower than the digits going into it, which is where typos come
              from - and a mistyped phone is a lead you cannot reach. */}
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <PhoneField
              id="phone"
              value={formData.phone}
              iso={phoneIso}
              onChange={(e164) => setField('phone', e164)}
              onBlur={capturePartial}
            />
          </div>

          {(collectCompany || collectWebsite) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {collectCompany && (
                <div className="space-y-1.5">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setField('company', e.target.value)}
                    placeholder="Your company"
                    autoComplete="organization"
                  />
                </div>
              )}
              {collectWebsite && (
                <div className="space-y-1.5">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="text"
                    inputMode="url"
                    value={formData.website}
                    onChange={(e) => setField('website', e.target.value)}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && !/^https?:\/\//i.test(v) && /\.[a-z]{2,}/i.test(v)) {
                        setField('website', `https://${v}`);
                      }
                    }}
                    placeholder="example.com"
                    autoComplete="url"
                  />
                </div>
              )}
            </div>
          )}
          </>)}

          {/* This page's custom fields from the Form Builder schema. */}
          {pageFields.length > 0 && (
            <div className="space-y-3">
              {pageFields.map((field) => (
                <CustomFieldInput
                  key={field.id}
                  field={field}
                  value={formData.custom_data?.[field.id]}
                  otherTexts={formData.custom_data}
                  onChange={setCustom}
                  onOtherChange={setOtherText}
                />
              ))}
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-md border border-destructive-soft bg-destructive-soft/60 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>

        <div className="space-y-3 border-t border-border bg-muted/30 px-6 py-4">
          {!isLastPage ? (
            <div className="flex items-center justify-between">
              {pageIdx > 0 ? (
                <Button type="button" variant="ghost" onClick={goBack}>Back</Button>
              ) : <span />}
              <Button type="button" size="lg" onClick={goNext}>Continue</Button>
            </div>
          ) : (<>
          {formConfig?.confirm_text && (
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-primary"
              />
              <span>{formConfig.confirm_text}</span>
            </label>
          )}
          {verification.active && (
            <VerifyGate
              v={verification}
              phone={formData.phone}
              onPhoneChange={(e164) => setField('phone', e164)}
              busy={submitting}
            />
          )}
          <div className={`flex items-center ${stepNums.length > 1 ? 'justify-between' : 'justify-end'}`}>
            {stepNums.length > 1 && <Button type="button" variant="ghost" onClick={goBack}>Back</Button>}
            <Button type="submit" disabled={submitting || (!!formConfig?.confirm_text && !confirmed)} size="lg">
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Sending&hellip;
                </>
              ) : (
                <>
                  <Send className="size-4" />
                  {verification.active && !verification.verified
                    ? `Verify & ${(formConfig?.submit_label || 'Submit').toLowerCase()}`
                    : (formConfig?.submit_label || 'Submit')}
                </>
              )}
            </Button>
          </div>
          {formConfig?.cta_note && (
            <p className="text-right text-xs text-muted-foreground">{formConfig.cta_note}</p>
          )}
          {formConfig?.privacy_note && (
            <p className="text-right text-[11px] text-muted-foreground/80">{formConfig.privacy_note}</p>
          )}
          </>)}
        </div>
      </form>
    </Card>
  );
}
